// =====================================================================
// Contratos (e-sign / S104 F1) — painel de assinatura de contratos. Listar +
// criar (modelo|upload) + o MÉDICO assinar com ICP + baixar/cancelar. Gated por
// esign_enabled (o menu já esconde; aqui também degrada). Paciente assina eletrônica
// = Fase 2 (o signatário paciente fica pendente). Padrão MUI v4.
// =====================================================================
import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper, Typography, Button, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, Tooltip, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Box, FormControlLabel, Checkbox
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DescriptionIcon from "@material-ui/icons/Description";
import GetAppIcon from "@material-ui/icons/GetApp";
import CreateIcon from "@material-ui/icons/Create";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import SendIcon from "@material-ui/icons/Send";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import LinkIcon from "@material-ui/icons/Link";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import { toast } from "react-toastify";
import {
  getEsignConfig, listEsignDocuments, createEsignFromTemplate, createEsignFromUpload,
  signEsignAsDoctor, cancelEsignDocument, sendEsignForSignature, openEsignPdf,
  openEsignManifest, esignVerifyUrl, listEsignTemplates, getEsignTemplate,
  upsertEsignTemplate, deleteEsignTemplate,
  listProfessionalsCert, uploadProfessionalCert, removeProfessionalCert, esignErrorMessage
} from "../../services/esignService";
import { listPatients } from "../../services/patientService";
import { listEncounterProfessionals } from "../../services/encounterService";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  header: { display: "flex", alignItems: "center", marginBottom: theme.spacing(2), gap: 12, flexWrap: "wrap" }
}));

const STATUS_LABEL = {
  rascunho: "Rascunho", aguardando: "Aguardando assinaturas",
  assinado: "Assinado", cancelado: "Cancelado", expirado: "Expirado"
};
const STATUS_COLOR = {
  rascunho: "#757575", aguardando: "#f57c00", assinado: "#2e7d32",
  cancelado: "#c62828", expirado: "#9e9e9e"
};

const PAGE_SIZE = 20;

const Contratos = () => {
  const classes = useStyles();
  const [enabled, setEnabled] = useState(null); // null = carregando
  const [docs, setDocs] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [sendLinks, setSendLinks] = useState(null); // [F2] links de assinatura do paciente
  const [tplOpen, setTplOpen] = useState(false); // [F4] editor de modelos
  const [certOpen, setCertOpen] = useState(false); // [S107] certificados ICP

  useEffect(() => {
    getEsignConfig()
      .then((c) => setEnabled(!!(c && c.enabled)))
      .catch(() => setEnabled(false));
  }, []);

  const fetchDocs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const data = await listEsignDocuments({ page: p, pageSize: PAGE_SIZE });
      setDocs(Array.isArray(data && data.documents) ? data.documents : []);
      setCount(Number(data && data.count) || 0);
      setPage(p);
    } catch (e) {
      toast.error(esignErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (enabled) fetchDocs(1); }, [enabled, fetchDocs]);

  const handleSign = async (id) => {
    setBusyId(id);
    try {
      await signEsignAsDoctor(id);
      toast.success("Documento assinado pelo médico (ICP-Brasil). ✅");
      fetchDocs(page);
    } catch (e) {
      toast.error(esignErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Cancelar este documento?")) return;
    setBusyId(id);
    try {
      await cancelEsignDocument(id);
      toast.info("Documento cancelado.");
      fetchDocs(page);
    } catch (e) {
      toast.error(esignErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  // [F2] gera o link de assinatura do paciente e abre o diálogo pra copiar.
  const handleSend = async (id) => {
    setBusyId(id);
    try {
      const r = await sendEsignForSignature(id);
      setSendLinks(Array.isArray(r && r.links) ? r.links : []);
      fetchDocs(page);
    } catch (e) {
      toast.error(esignErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  if (enabled === null) {
    return <div className={classes.root}><CircularProgress /></div>;
  }
  if (!enabled) {
    return (
      <div className={classes.root}>
        <Typography color="textSecondary">
          O módulo de Contratos não está habilitado para esta clínica.
        </Typography>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.header}>
        <DescriptionIcon color="primary" />
        <Typography variant="h6">Contratos</Typography>
        <Box flexGrow={1} />
        <Typography variant="body2" color="textSecondary">{count} documento(s)</Typography>
        <Button variant="outlined" color="primary" startIcon={<VerifiedUserIcon />} onClick={() => setCertOpen(true)}>
          Certificados ICP
        </Button>
        <Button variant="outlined" color="primary" startIcon={<DescriptionIcon />} onClick={() => setTplOpen(true)}>
          Modelos
        </Button>
        <Button variant="contained" color="primary" onClick={() => setDlgOpen(true)}>
          Novo contrato
        </Button>
      </div>

      {loading ? (
        <CircularProgress />
      ) : docs.length === 0 ? (
        <Typography color="textSecondary">Nenhum contrato ainda. Clique em “Novo contrato”.</Typography>
      ) : (
        <div style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small" style={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Paciente</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Assinaturas</TableCell>
                <TableCell align="center" style={{ width: 184 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((d) => {
                const closed = d.status === "assinado" || d.status === "cancelado";
                return (
                  <TableRow key={d.id} hover>
                    <TableCell>{d.title}</TableCell>
                    <TableCell>{d.patientName || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={STATUS_LABEL[d.status] || d.status}
                        style={{ backgroundColor: STATUS_COLOR[d.status] || "#757575", color: "#fff" }} />
                      {d.sealed && (
                        <Tooltip title="Selado com certificado ICP-Brasil da clínica">
                          <VerifiedUserIcon fontSize="small" style={{ color: "#2e7d32", verticalAlign: "middle", marginLeft: 6 }} />
                        </Tooltip>
                      )}
                      {d.expiresAt && d.status !== "assinado" && d.status !== "cancelado" && (
                        <div style={{ fontSize: 11, marginTop: 2, color: new Date(d.expiresAt) < new Date() ? "#c62828" : "#f57c00" }}>
                          {new Date(d.expiresAt) < new Date() ? "⏳ expirado" : `⏳ expira ${new Date(d.expiresAt).toLocaleDateString("pt-BR")}`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell align="center">{d.signersDone}/{d.signersTotal}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Baixar / ver o PDF">
                        <IconButton size="small" onClick={() => openEsignPdf(d.id).catch(() => toast.error("Não foi possível abrir o PDF."))}>
                          <GetAppIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {d.status === "assinado" && d.hasManifest && (
                        <Tooltip title="Baixar o manifesto (prova das assinaturas)">
                          <IconButton size="small" onClick={() => openEsignManifest(d.id).catch(() => toast.error("Manifesto ainda não disponível."))}>
                            <AssignmentTurnedInIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {d.status === "assinado" && d.verificationCode && (
                        <Tooltip title="Copiar link público de verificação">
                          <IconButton size="small" onClick={() => {
                            const u = esignVerifyUrl(d.verificationCode);
                            if (navigator.clipboard) navigator.clipboard.writeText(u);
                            toast.success("Link de verificação copiado!");
                          }}>
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!closed && (
                        <Tooltip title="Enviar para o paciente assinar (link)">
                          <span>
                            <IconButton size="small" disabled={busyId === d.id} onClick={() => handleSend(d.id)}>
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {!closed && (
                        <Tooltip title="Médico assinar (ICP-Brasil)">
                          <span>
                            <IconButton size="small" color="primary" disabled={busyId === d.id} onClick={() => handleSign(d.id)}>
                              {busyId === d.id ? <CircularProgress size={16} /> : <CreateIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {!closed && (
                        <Tooltip title="Cancelar documento">
                          <span>
                            <IconButton size="small" style={{ color: "#c62828" }} disabled={busyId === d.id} onClick={() => handleCancel(d.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Box mt={1} display="flex" alignItems="center" style={{ gap: 12 }}>
        <Button size="small" disabled={page <= 1} onClick={() => fetchDocs(page - 1)}>Anterior</Button>
        <Typography variant="body2">Página {page} de {totalPages}</Typography>
        <Button size="small" disabled={page >= totalPages} onClick={() => fetchDocs(page + 1)}>Próxima</Button>
      </Box>

      <NewContractDialog open={dlgOpen} onClose={() => setDlgOpen(false)} onSaved={() => { setDlgOpen(false); fetchDocs(1); }} />

      <TemplatesDialog open={tplOpen} onClose={() => setTplOpen(false)} />

      <CertificatesDialog open={certOpen} onClose={() => setCertOpen(false)} />

      {/* [F2] Links de assinatura do paciente — copiar e enviar (WhatsApp/e-mail). */}
      <Dialog open={!!sendLinks} onClose={() => setSendLinks(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Link de assinatura do paciente</DialogTitle>
        <DialogContent>
          {sendLinks && sendLinks.length === 0 && (
            <Typography color="textSecondary">Nenhum paciente pendente neste documento.</Typography>
          )}
          {sendLinks && sendLinks.map((l) => (
            <Box key={l.signerId} mb={2}>
              <Typography variant="subtitle2">{l.name}{l.contactWa ? ` — ${l.contactWa}` : ""}</Typography>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <TextField value={l.url} fullWidth size="small" variant="outlined" margin="dense"
                  InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
                <Button size="small" variant="outlined" onClick={() => {
                  if (navigator.clipboard) navigator.clipboard.writeText(l.url);
                  toast.success("Link copiado!");
                }}>Copiar</Button>
              </Box>
              {l.message && (
                <Button size="small" color="primary" startIcon={<SendIcon />} style={{ marginTop: 4 }} onClick={() => {
                  if (navigator.clipboard) navigator.clipboard.writeText(l.message);
                  toast.success("Mensagem copiada! Cole no WhatsApp do paciente.");
                }}>Copiar mensagem pronta</Button>
              )}
            </Box>
          ))}
          <Typography variant="caption" color="textSecondary">
            Envie o link ao paciente (WhatsApp/e-mail). Ao abrir, ele lê o documento, consente e assina — a prova (IP/hora/dispositivo) é registrada.
          </Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setSendLinks(null)}>Fechar</Button></DialogActions>
      </Dialog>
    </Paper>
  );
};

// ---------------------------------------------------------------------
// Diálogo "Novo contrato" — modelo OU upload + paciente + médico (ICP) + (F2) paciente.
// ---------------------------------------------------------------------
const NewContractDialog = ({ open, onClose, onSaved }) => {
  const [mode, setMode] = useState("template"); // template|upload
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [patient, setPatient] = useState(null);
  const [patientOpts, setPatientOpts] = useState([]);
  const [patientLoading, setPatientLoading] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [professionalId, setProfessionalId] = useState("");
  const [patientSigns, setPatientSigns] = useState(false); // paciente também assina (F2)
  const [expiresAt, setExpiresAt] = useState(""); // [F4] validade opcional
  const [saving, setSaving] = useState(false);
  const debRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setMode("template"); setTemplateId(""); setFile(null); setTitle("");
    setPatient(null); setPatientOpts([]); setProfessionalId(""); setPatientSigns(false); setExpiresAt(""); setSaving(false);
    listEsignTemplates().then((d) => setTemplates(Array.isArray(d && d.templates) ? d.templates : [])).catch(() => setTemplates([]));
    listEncounterProfessionals().then((d) => setProfessionals(Array.isArray(d && d.professionals) ? d.professionals : [])).catch(() => setProfessionals([]));
  }, [open]);

  const searchPatients = (term) => {
    if (debRef.current) clearTimeout(debRef.current);
    if (!term || term.trim().length < 2) { setPatientOpts([]); return; }
    debRef.current = setTimeout(async () => {
      setPatientLoading(true);
      try {
        const d = await listPatients({ search: term, pageSize: 10 });
        setPatientOpts(Array.isArray(d && d.patients) ? d.patients : []);
      } catch (e) {
        setPatientOpts([]);
      } finally {
        setPatientLoading(false);
      }
    }, 400);
  };

  const buildSigners = () => {
    const signers = [];
    const prof = professionals.find((p) => String(p.id) === String(professionalId));
    signers.push({ role: "medico", professionalId: Number(professionalId), name: (prof && prof.name) || "Médico", methodRequired: "icp" });
    if (patientSigns && patient) {
      signers.push({ role: "paciente", name: patient.fullName, cpf: patient.cpf || undefined, contactWa: patient.phone || undefined, methodRequired: "any" });
    }
    return signers;
  };

  const handleSave = async () => {
    if (!professionalId) { toast.error("Selecione o médico que vai assinar."); return; }
    if (mode === "template" && !templateId) { toast.error("Selecione o modelo."); return; }
    if (mode === "upload" && !file) { toast.error("Selecione o PDF do contrato."); return; }
    if (patientSigns && !patient) { toast.error("Selecione o paciente (ele vai assinar)."); return; }
    setSaving(true);
    try {
      const signers = buildSigners();
      if (mode === "template") {
        await createEsignFromTemplate({
          templateId: Number(templateId),
          patientId: patient ? patient.id : undefined,
          title: title || undefined,
          expiresAt: expiresAt || undefined,
          signers
        });
      } else {
        const fd = new FormData();
        fd.append("file", file);
        if (patient) fd.append("patientId", String(patient.id));
        if (title) fd.append("title", title);
        if (expiresAt) fd.append("expiresAt", expiresAt);
        fd.append("signers", JSON.stringify(signers));
        await createEsignFromUpload(fd);
      }
      toast.success("Contrato criado. ✅");
      onSaved && onSaved();
    } catch (e) {
      toast.error(esignErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Novo contrato</DialogTitle>
      <DialogContent>
        <TextField select label="Origem" value={mode} onChange={(e) => setMode(e.target.value)} fullWidth margin="dense">
          <MenuItem value="template">Modelo do sistema (com dados do paciente)</MenuItem>
          <MenuItem value="upload">Subir um PDF pronto</MenuItem>
        </TextField>

        {mode === "template" ? (
          <TextField select label="Modelo" value={templateId} onChange={(e) => setTemplateId(e.target.value)} fullWidth margin="dense"
            helperText={templates.length === 0 ? "Nenhum modelo cadastrado ainda." : ""}>
            {templates.map((t) => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
          </TextField>
        ) : (
          <Box mt={1} mb={1}>
            <Button component="label" variant="outlined" size="small">
              {file ? "Trocar PDF" : "📎 Selecionar PDF"}
              <input type="file" accept="application/pdf" hidden
                onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; setFile(f || null); }} />
            </Button>
            {file && <Typography variant="caption" style={{ marginLeft: 8 }}>{file.name}</Typography>}
          </Box>
        )}

        <TextField label="Título (opcional)" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth margin="dense" />

        <TextField label="Validade (opcional)" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
          fullWidth margin="dense" InputLabelProps={{ shrink: true }}
          helperText="Depois desta data o documento não pode mais ser assinado." />

        <Autocomplete
          options={patientOpts} value={patient} loading={patientLoading}
          getOptionLabel={(o) => (o && o.fullName) || ""}
          getOptionSelected={(o, v) => o.id === (v && v.id)}
          onChange={(e, v) => setPatient(v)}
          onInputChange={(e, v) => searchPatients(v)}
          noOptionsText="Digite ao menos 2 letras…"
          renderInput={(params) => (
            <TextField {...params} label="Paciente (opcional)" margin="dense" variant="outlined" placeholder="Nome / CPF / telefone"
              InputProps={{ ...params.InputProps, endAdornment: (<>{patientLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>) }} />
          )}
        />

        <TextField select label="Médico que assina (ICP-Brasil)" value={professionalId} onChange={(e) => setProfessionalId(e.target.value)} fullWidth margin="dense"
          helperText={professionals.length === 0 ? "Nenhum profissional encontrado." : "Precisa ter certificado ICP configurado."}>
          {professionals.map((p) => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
        </TextField>

        <FormControlLabel
          control={<Checkbox checked={patientSigns} onChange={(e) => setPatientSigns(e.target.checked)} />}
          label="Paciente também assina (assinatura eletrônica — envio por WhatsApp na Fase 2)"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>
          Criar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------
// [F4] Diálogo "Modelos" — CRUD de templates com {{variáveis}}. Backend já pronto.
// ---------------------------------------------------------------------
const VARS_HELP = ["{{nome}}", "{{cpf}}", "{{telefone}}", "{{valor}}", "{{data}}"];

const TemplatesDialog = ({ open, onClose }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | { id?, name, type, body, active }
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listEsignTemplates();
      setList(Array.isArray(d && d.templates) ? d.templates : []);
    } catch (e) {
      toast.error(esignErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) { setEditing(null); fetchList(); } }, [open, fetchList]);

  const openEdit = async (id) => {
    if (!id) { setEditing({ name: "", type: "contrato", body: "", active: true }); return; }
    try {
      const t = await getEsignTemplate(id);
      setEditing({ id: t.id, name: t.name || "", type: t.type || "contrato", body: t.body || "", active: t.active !== false });
    } catch (e) {
      toast.error(esignErrorMessage(e));
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    if (!editing.name.trim()) { toast.error("Informe o nome do modelo."); return; }
    if (!editing.body.trim()) { toast.error("O corpo do modelo não pode ficar vazio."); return; }
    setSaving(true);
    try {
      await upsertEsignTemplate({ id: editing.id, name: editing.name, type: editing.type, body: editing.body, active: editing.active });
      toast.success("Modelo salvo. ✅");
      setEditing(null);
      fetchList();
    } catch (e) {
      toast.error(esignErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Desativar este modelo? (não apaga o histórico)")) return;
    try {
      await deleteEsignTemplate(id);
      toast.info("Modelo desativado.");
      fetchList();
    } catch (e) {
      toast.error(esignErrorMessage(e));
    }
  };

  const insertVar = (v) => setEditing((e) => ({ ...e, body: (e.body || "") + v }));

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Modelos de contrato</DialogTitle>
      <DialogContent dividers>
        {editing ? (
          <Box>
            <TextField label="Nome do modelo" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} fullWidth margin="dense" />
            <TextField select label="Tipo" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} fullWidth margin="dense">
              <MenuItem value="contrato">Contrato</MenuItem>
              <MenuItem value="termo">Termo</MenuItem>
              <MenuItem value="outro">Outro</MenuItem>
            </TextField>
            <Box mt={1} mb={0.5}>
              <Typography variant="caption" color="textSecondary">Variáveis (clique p/ inserir): </Typography>
              {VARS_HELP.map((v) => (
                <Chip key={v} size="small" label={v} onClick={() => insertVar(v)} style={{ margin: "0 4px 4px 0", cursor: "pointer" }} />
              ))}
            </Box>
            <TextField label="Corpo do documento" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
              fullWidth margin="dense" multiline minRows={10} variant="outlined"
              placeholder="Ex.: Pelo presente instrumento, {{nome}}, CPF {{cpf}}, contrata os serviços da clínica..." />
            <FormControlLabel control={<Checkbox checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />} label="Ativo" />
          </Box>
        ) : loading ? (
          <CircularProgress />
        ) : list.length === 0 ? (
          <Typography color="textSecondary">Nenhum modelo ainda. Clique em “Novo modelo”.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell><TableCell>Tipo</TableCell>
                <TableCell align="center">Ativo</TableCell><TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.type}</TableCell>
                  <TableCell align="center">{t.active ? "Sim" : "Não"}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openEdit(t.id)}>Editar</Button>
                    {t.active && <Button size="small" style={{ color: "#c62828" }} onClick={() => handleDeactivate(t.id)}>Desativar</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        {editing ? (
          <>
            <Button onClick={() => setEditing(null)} disabled={saving}>Voltar</Button>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>Salvar</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Fechar</Button>
            <Button variant="contained" color="primary" onClick={() => openEdit(null)}>Novo modelo</Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------
// [S107] Diálogo "Certificados ICP" — o médico sobe o próprio .pfx (self-service).
// O arquivo fica só no servidor (privado); a senha vai cifrada no banco. Nunca em claro.
// ---------------------------------------------------------------------
const CertificatesDialog = ({ open, onClose }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [upFor, setUpFor] = useState(null); // profissional em configuração { id, name }
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listProfessionalsCert();
      setList(Array.isArray(d && d.professionals) ? d.professionals : []);
    } catch (e) {
      toast.error(esignErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) { setUpFor(null); setFile(null); setPassword(""); fetchList(); } }, [open, fetchList]);

  const handleUpload = async () => {
    if (!upFor) return;
    if (!file) { toast.error("Selecione o arquivo .pfx."); return; }
    if (!password) { toast.error("Informe a senha do certificado."); return; }
    setSaving(true);
    try {
      const r = await uploadProfessionalCert(upFor.id, file, password);
      toast.success(`Certificado configurado${r && r.validated === false ? " (será validado na 1ª assinatura)" : ""}. ✅`);
      setUpFor(null); setFile(null); setPassword("");
      fetchList();
    } catch (e) {
      toast.error(esignErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remover o certificado deste médico?")) return;
    try {
      await removeProfessionalCert(id);
      toast.info("Certificado removido.");
      fetchList();
    } catch (e) {
      toast.error(esignErrorMessage(e));
    }
  };

  const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString("pt-BR") : "—"; } catch (e) { return "—"; } };
  const isExpired = (d) => { try { return d && new Date(d) < new Date(); } catch (e) { return false; } };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Certificados ICP (assinatura do médico)</DialogTitle>
      <DialogContent dividers>
        {upFor ? (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Configurar certificado — {upFor.name}</Typography>
            <Box mt={1} mb={1}>
              <Button component="label" variant="outlined" size="small">
                {file ? "Trocar .pfx" : "📎 Selecionar .pfx / .p12"}
                <input type="file" accept=".pfx,.p12" hidden
                  onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; setFile(f || null); }} />
              </Button>
              {file && <Typography variant="caption" style={{ marginLeft: 8 }}>{file.name}</Typography>}
            </Box>
            <TextField label="Senha do certificado" type="password" value={password}
              onChange={(e) => setPassword(e.target.value)} fullWidth margin="dense" autoComplete="new-password" />
            <Typography variant="caption" color="textSecondary">
              🔒 O arquivo fica só no servidor (privado) e a senha é cifrada. Nunca trafega em texto puro nem aparece em logs.
            </Typography>
          </Box>
        ) : loading ? (
          <CircularProgress />
        ) : list.length === 0 ? (
          <Typography color="textSecondary">Nenhum profissional encontrado.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Médico</TableCell><TableCell>Certificado</TableCell><TableCell align="right">Ações</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {list.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{p.name}</TableCell>
                  <TableCell>
                    {p.hasCert ? (
                      <span style={{ color: isExpired(p.validUntil) ? "#c62828" : "#2e7d32" }}>
                        {isExpired(p.validUntil) ? "⚠️ vencido" : "✓ configurado"}
                        {p.validUntil ? ` (val. ${fmtDate(p.validUntil)})` : ""}
                      </span>
                    ) : (
                      <span style={{ color: "#9e9e9e" }}>sem certificado</span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => { setUpFor({ id: p.id, name: p.name }); setFile(null); setPassword(""); }}>
                      {p.hasCert ? "Trocar" : "Configurar"}
                    </Button>
                    {p.hasCert && <Button size="small" style={{ color: "#c62828" }} onClick={() => handleRemove(p.id)}>Remover</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        {upFor ? (
          <>
            <Button onClick={() => setUpFor(null)} disabled={saving}>Voltar</Button>
            <Button variant="contained" color="primary" onClick={handleUpload} disabled={saving} startIcon={saving ? <CircularProgress size={16} /> : null}>Salvar certificado</Button>
          </>
        ) : (
          <Button onClick={onClose}>Fechar</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default Contratos;
